import React, { useEffect, useState, useRef } from "react";
import { Loader2 } from "lucide-react";
import { ChatRoles } from "../../app/Enums/ChatRoles";
import "./ChatCommon.css";
import "./User_Bot.css";
import {
  getUsers,
  getUserBotChatDates,
  getUserBotChats,
  GetUsersSessionByEmail,
} from "../../Apis_chatbot/apis";
import Pagination from "../../app/components/Pagination/Pagination";
import MarkdownMessage from "../../app/components/MarkdownMessage/MarkdownMessage";

// Human-readable role name from the shared ChatRoles enum.
const getRoleName = (roleId) => ChatRoles[roleId] || "Unknown";

// Bubble variant for a given chat role.
const getRoleVariant = (roleId) =>
  roleId === ChatRoles.User ? "user" : roleId === ChatRoles.Agent ? "agent" : "bot";

// First letter for a monogram avatar.
const getInitial = (value) => {
  const text = (value || "").trim();
  return text ? text[0].toUpperCase() : "?";
};

const formatDay = (value) =>
  new Date(value).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const formatTime = (value) =>
  new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export default function User_Bot_Conversation() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [expandedUser, setExpandedUser] = useState(null);
  const [userSessions, setUserSessions] = useState({});
  const [selectedSession, setSelectedSession] = useState(null);
  const [chatDates, setChatDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [chats, setChats] = useState([]);

  // Loaders for the date list and the message list.
  const [datesLoading, setDatesLoading] = useState(false);
  const [chatsLoading, setChatsLoading] = useState(false);

  const chatMessagesRef = useRef(null);

  // Fetch users
  useEffect(() => {
    fetchUsers();
  }, [currentPage, pageSize]);

  const fetchUsers = async () => {
    setLoading(true);
    const req = { pageNumber: currentPage, pageSize: pageSize };
    const res = await getUsers(req);
    if (res.result == 1 && !res.error) {
      setData(res.result_value || []);
      setTotalRows(res.total_row || 0);
    } else {
      setData([]);
      setTotalRows(0);
    }
    setLoading(false);
  };

  // --- Handle User Click ---
  const handleUserClick = async (user) => {
    // Collapse toggle
    if (expandedUser?.email !== null && expandedUser?.email === user.email) {
      setExpandedUser(null);
      setUserSessions({});
      setSelectedSession(null);
      setChatDates([]);
      setChats([]);
      return;
    }

    setExpandedUser(user);
    setSelectedSession(null);
    setSelectedDate(null);
    setChatDates([]);
    setChats([]);

    const hasEmail =
      user?.email && user?.email !== "" && user?.email != null && user?.email !== undefined;

    setDatesLoading(true);

    try {
      if (hasEmail) {
        // Fetch sessions
        const sessionRes = await GetUsersSessionByEmail(user.email);

        if (sessionRes?.result_value?.length > 0) {
          // 🟢 User has sessions → show them (dates load after a session is picked)
          setUserSessions((prev) => ({
            ...prev,
            [user.email]: sessionRes.result_value,
          }));
          return;
        }

        // 🔵 No sessions → directly fetch chat dates for this user
        setUserSessions((prev) => ({ ...prev, [user.email]: [] }));
      } else {
        setUserSessions((prev) => ({ ...prev, [user.email]: [] }));
      }

      const res = await getUserBotChatDates(user.userId);
      setChatDates(res?.result_value || []);
      setSelectedSession({ userId: user.userId, name: user.name });
    } finally {
      setDatesLoading(false);
    }
  };

  const getHeaderData = () => {
    //  Nothing selected
    if (!selectedSession) {
      return {
        text: "No conversation selected",
        showIcon: false,
      };
    }

    const name = selectedSession.name?.trim();
    const email = selectedSession.email?.trim();

    // Name exists → highest priority
    if (name) {
      return {
        text: name,
        showIcon: true,
      };
    }

    // Name missing, email exists
    if (email) {
      return {
        text: email,
        showIcon: true,
      };
    }

    // Nothing exists
    return {
      text: "Anonymous User",
      showIcon: true,
    };
  };

  const headerData = getHeaderData();

  // --- Handle Session Click ---
  const handleSessionClick = async (session) => {
    setSelectedSession(session);
    setSelectedDate(null);
    setChats([]);
    setChatDates([]);

    setDatesLoading(true);

    try {
      const res = await getUserBotChatDates(session.userId);
      setChatDates(res?.result_value || []);
    } finally {
      setDatesLoading(false);
    }
  };

  // --- Handle Date Click ---
  const handleDateClick = async (chatDate) => {
    setSelectedDate(chatDate);
    setChats([]);

    setChatsLoading(true);

    try {
      const res = await getUserBotChats(
        selectedSession?.userId || 0,
        chatDate.chatDate
      );
      setChats(res?.result_value || []);
    } finally {
      setChatsLoading(false);
    }
  };

  // --- Auto-scroll chat window ---
  useEffect(() => {
    const el = chatMessagesRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [chats, selectedDate]);

  const totalPages = Math.ceil(totalRows / pageSize);

  const chatSubtitle = !selectedSession
    ? "Pick a user, then a session and a date"
    : selectedDate
      ? `Conversation on ${formatDay(selectedDate.chatDate)}`
      : "Select a date to view messages";

  return (
    <div className="uc-page">
      {/* HEADER */}
      <header className="uc-header">
        <div className="uc-header-titles">
          <h1>User &ndash; Bot Conversations</h1>
          <p>Review chatbot conversations by user, session and date.</p>
        </div>

        {totalRows > 0 && (
          <div className="uc-header-stat">
            {totalRows} {totalRows === 1 ? "user" : "users"}
          </div>
        )}
      </header>

      {/* WORKSPACE */}
      <div className="uc-shell">
        {/* SIDEBAR — users & sessions */}
        <aside className="uc-sidebar">
          <div className="uc-sidebar-head">
            <h2>Users</h2>
            {!loading && data.length > 0 && (
              <span className="uc-count">{data.length} shown</span>
            )}
          </div>

          <div className="uc-sidebar-body">
            {loading ? (
              <div className="uc-inline-loader">
                <Loader2 className="uc-spin" size={20} />
                <span>Loading users&hellip;</span>
              </div>
            ) : data.length === 0 ? (
              <div className="uc-list-empty">No users found</div>
            ) : (
              data.map((user, index) => {
                const isExpanded = expandedUser?.email === user.email;
                const sessions = userSessions[user.email] || [];
                const displayName =
                  user.name  || "Anonymous user" ;

                return (
                  <div key={index} className="uc-conv-group">
                    <div
                      role="button"
                      tabIndex={0}
                      className={`uc-conv ${
                        expandedUser?.userId === user.userId ? "uc-conv--active" : ""
                      }`}
                      onClick={() => handleUserClick(user)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleUserClick(user);
                        }
                      }}
                    >
                      <div className="uc-conv-top">
                        <span className="uc-avatar">{getInitial(displayName)}</span>

                        <span className="uc-conv-ident">
                          <strong title={displayName}>{displayName}</strong>
                          <span title={user.email || undefined}>
                            {user.email || "No email on record"}
                          </span>
                        </span>

                        <span className="uc-conv-chevron" aria-hidden="true">
                          {isExpanded ? "−" : "+"}
                        </span>
                      </div>

                      <dl className="uc-conv-meta">
                        <div>
                          <dt>Identity</dt>
                          <dd title={user.uniqueIdentity || undefined}>
                            {user.uniqueIdentity || "—"}
                          </dd>
                        </div>
                        <div>
                          <dt>Joined</dt>
                          <dd>
                            {user.createdAt
                              ? new Date(user.createdAt).toLocaleDateString()
                              : "—"}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {isExpanded && sessions.length > 0 && (
                      <div className="uc-session-list">
                        {sessions.map((session, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`uc-session ${
                              selectedSession?.userId === session.userId
                                ? "uc-session--active"
                                : ""
                            }`}
                            onClick={() => handleSessionClick(session)}
                          >
                            <span className="uc-session-title">
                              Session #{idx + 1}
                            </span>
                            <span className="uc-session-sub">
                              {session.name || "N/A"}
                              {session.createdAt
                                ? ` · ${new Date(
                                    session.createdAt
                                  ).toLocaleString()}`
                                : ""}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {totalPages > 1 && (
            <div className="uc-sidebar-foot">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                pageSize={pageSize}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </aside>

        {/* CHAT PANEL */}
        <section className="uc-chat">
          <div className="uc-chat-head">
            <span
              className={`uc-avatar ${headerData.showIcon ? "" : "uc-avatar--bot"}`}
            >
              {headerData.showIcon ? getInitial(headerData.text) : "—"}
            </span>

            <div className="uc-chat-head-text">
              <strong title={headerData.text}>{headerData.text}</strong>
              <span>{chatSubtitle}</span>
            </div>

            {selectedDate && chats.length > 0 && (
              <span className="uc-chat-head-badge">
                {chats.length} {chats.length === 1 ? "message" : "messages"}
              </span>
            )}
          </div>

          {selectedSession ? (
            <div className="uc-chat-body">
              {/* DATE RAIL */}
              <div className="uc-dates">
                <div className="uc-dates-title">Chat dates</div>

                {datesLoading ? (
                  <div className="uc-inline-loader uc-inline-loader--sm">
                    <Loader2 className="uc-spin" size={16} />
                    <span>Loading&hellip;</span>
                  </div>
                ) : chatDates.length === 0 ? (
                  <div className="uc-dates-empty">No chat dates found</div>
                ) : (
                  chatDates.map((d, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`uc-date ${
                        selectedDate?.chatDate === d.chatDate ? "uc-date--active" : ""
                      }`}
                      onClick={() => handleDateClick(d)}
                    >
                      {formatDay(d.chatDate)}
                    </button>
                  ))
                )}
              </div>

              {/* MESSAGES */}
              <div className="uc-messages" ref={chatMessagesRef}>
                {!selectedDate ? (
                  <div className="uc-state">
                    <div className="uc-state-icon" aria-hidden="true">
                      🗓️
                    </div>
                    <h3>Pick a date</h3>
                    <p>Choose a date from the list to view that day's conversation.</p>
                  </div>
                ) : chatsLoading ? (
                  <div className="uc-inline-loader">
                    <Loader2 className="uc-spin" size={22} />
                    <p>Loading conversation&hellip;</p>
                  </div>
                ) : chats.length === 0 ? (
                  <div className="uc-state">
                    <div className="uc-state-icon" aria-hidden="true">
                      💬
                    </div>
                    <h3>No messages</h3>
                    <p>There are no messages recorded for this date.</p>
                  </div>
                ) : (
                  chats.map((c, idx) => {
                    const roleId = c.sender_chatRole_id;
                    const variant = getRoleVariant(roleId);

                    return (
                      <div key={idx} className={`uc-row uc-row--${variant}`}>
                        <div className={`uc-bubble uc-bubble--${variant}`}>
                          <div className="uc-bubble-top">
                            <span className="uc-bubble-role">
                              {getRoleName(roleId)}
                            </span>
                            <span className="uc-bubble-time">
                              {c.sendedTime ? formatTime(c.sendedTime) : ""}
                            </span>
                          </div>
                          <div className="uc-bubble-text">
                            <MarkdownMessage text={c.sendedMessage} />
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ) : (
            <div className="uc-chat-body">
              <div className="uc-state">
                <div className="uc-state-icon" aria-hidden="true">
                  💬
                </div>
                <h3>No conversation selected</h3>
                <p>
                  Select a user from the list &mdash; then choose a session (if shown)
                  and a date to view the chat history.
                </p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
